<template>
    <div class="recommended">
        <div class="recommended__title">
            <strong>{{ $t('rating.averageRating', averageRating) }}</strong>
            {{ $t('rating.totalRating', totalRating) }}
        </div>

        <Recommend class="recommended__progress" :items="ratingsItems" />

        <div class="recommended__info">
            <img
                v-lazy-load
                :alt="$t('productDetail.rating.recommend')"
                src="/nuxt-assets/images/lazy-load-error.svg"
                data-src="/nuxt-assets/images/icons/like.svg"
                class="recommend__img"
                width="10"
                height="10"
            />
            <p class="recommended__text">
                {{ $t('rating.percentCustomersRecommended', recommendPercent) }}
            </p>
        </div>

        <nuxt-link :to="{ path: urlRating, hash: '#recommend' }" no-prefetch class="recommended__link">
            {{ $t('rating.showAllRatingTooltip', totalRating) }}
        </nuxt-link>
    </div>
</template>

<script lang="ts">
import ProductsRating from '~/models/Product/ProductsRating'
import RatingCount from '~/models/Product/RatingCount'
import RatingProgressBar from '~/models/Product/RatingProgressBar'
import Recommend from '~/components/elements/Recommend.vue'
import formatProductRating from '~/utils/formatProductRating'

export default {
    components: {
        Recommend,
    },
    props: {
        productId: {
            type: Number,
            required: true,
        },
        urlRating: {
            type: String,
            required: true,
        },
        productRating: {
            type: Object,
            default: null,
        },
    },
    data() {
        return {
            ratingsItems: [
                {
                    name: 5,
                    progress: 0,
                    count: '0',
                },
                {
                    name: 4,
                    progress: 0,
                    count: '0',
                },
                {
                    name: 3,
                    progress: 0,
                    count: '0',
                },
                {
                    name: 2,
                    progress: 0,
                    count: '0',
                },
                {
                    name: 1,
                    progress: 0,
                    count: '0',
                },
            ] as RatingProgressBar[],
            ratingsCounts: {} as RatingCount[],
            recommendPercent: 100 as number,
            averageRating: 5 as number,
            totalRating: 1 as number,
        }
    },
    created() {
        this.showRating(this.productId)
    },
    methods: {
        ratingCounts(index: number): number {
            return this.ratingsCounts[index]
        },

        formatRating(ratingsCounts: ProductsRating): RatingProgressBar[] {
            const ratingsItems = [] as RatingProgressBar[]
            const totalCount = ratingsCounts.total

            ratingsCounts.ratingCounts.forEach((index) => {
                ratingsItems.push({
                    name: index.rating,
                    progress: Math.round((index.value * 100) / totalCount),
                    count: `${index.value}x`,
                })
            })

            return ratingsItems.map((_e, i) => ratingsItems[ratingsItems.length - i - 1])
        },

        async showRating(productId: Number): Promise<void> {
            if (this.productRating === null) {
                const { productsRating } = await this.$repository.getProductRatingByIds([productId])
                this.ratingsItems = this.formatRating(productsRating[0])
                this.recommendPercent = productsRating[0].recommendationPercentage
                this.totalRating = productsRating[0].total
                this.averageRating = productsRating[0].averageRating

                return
            }

            this.recommendPercent = this.productRating.recommendPercentage
            this.totalRating = this.productRating.totalReviews
            this.averageRating = this.productRating.averageRating
            this.ratingsItems = formatProductRating(this.productRating)
        },
    },
}
</script>

<style lang="scss" scoped>
.recommended {
    width: 240px;
    min-height: 100px;
    text-align: center;
    padding: 15px 5px 25px 5px;
    background-color: $color-white;
    position: relative;
    z-index: 9999;
    box-shadow: $box-shadow;
    border-radius: $rounded;
    color: $color-dark;

    &__progress {
        margin: 0 25px;
    }

    &__info {
        background-color: $color-green-light;
        border-radius: 50px;
        color: $color-green-dark;
        padding: 0 10px;
        display: flex;
        width: 100%;
        margin: 10px 0;
        justify-content: center;
    }

    &__title {
        font-size: 14px;
    }

    &__text {
        font-weight: 600;
        font-size: 13px;
        margin: 3px 0 2px 5px;
        text-align: center;
    }

    &__link {
        font-size: 14px;
        font-weight: 600;
        text-decoration: underline;
    }
}
</style>
